import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Settings, Info, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'

export default function ResourceFilters() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [audienceType, setAudienceType] = useState<'all' | 'men' | 'women'>('all')
  const [ageFilter, setAgeFilter] = useState<boolean>(false)
  const [giftsRequired, setGiftsRequired] = useState<boolean>(false)
  const [premiumOnly, setPremiumOnly] = useState<boolean>(false)
  const [noPremiumOnly, setNoPremiumOnly] = useState<boolean>(false)
  const [language, setLanguage] = useState<'ru' | 'en' | 'all'>('all')
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      // Convert backend filters to miniapp state
      const filters = resource.filters || {}

      // Determine audience type from men/women booleans
      let audienceType: 'all' | 'men' | 'women' = 'all'
      if (filters.men && !filters.women) audienceType = 'men'
      else if (filters.women && !filters.men) audienceType = 'women'

      setAudienceType(audienceType)
      setAgeFilter(filters.age_18_plus || false)
      setGiftsRequired(filters.with_gifts || false)
      setPremiumOnly(filters.premium_only || false)
      setNoPremiumOnly(filters.no_premium_only || false)

      // Determine language from russian/english booleans
      let language: 'ru' | 'en' | 'all' = 'all'
      if (filters.russian && !filters.english) language = 'ru'
      else if (filters.english && !filters.russian) language = 'en'

      setLanguage(language)
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const handleSave = async () => {
    if (!resource) return

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      // Convert miniapp state to backend API format
      // Validate: cannot have both premium_only and no_premium_only
      if (premiumOnly && noPremiumOnly) {
        setError('Ресурс не может быть одновременно только для Premium и только для Non-Premium пользователей')
        hapticNotification('error')
        setMainButtonLoading(false)
        return
      }

      const filters = {
        men: audienceType === 'all' || audienceType === 'men',
        women: audienceType === 'all' || audienceType === 'women',
        age_18_plus: ageFilter,
        with_gifts: giftsRequired,
        premium_only: premiumOnly,
        no_premium_only: noPremiumOnly,
        russian: language === 'all' || language === 'ru',
        english: language === 'all' || language === 'en',
      }

      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: { filters },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      hideBackButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update filters:', error)
      setError('Ошибка при сохранении фильтров')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить фильтры', onSave, '#5288c1', '#ffffff')
    showBackButton(() => {
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`)
    })

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      hideBackButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, showBackButton, hideBackButton, setMainButtonLoading, navigate, resourceId])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const audienceOptions = [
    { value: 'all' as const, label: 'Все' },
    { value: 'men' as const, label: 'Мужчины' },
    { value: 'women' as const, label: 'Женщины' },
  ]

  const languageOptions = [
    { value: 'all' as const, label: 'Все языки', flag: '🌍' },
    { value: 'ru' as const, label: 'Русский', flag: '🇷🇺' },
    { value: 'en' as const, label: 'Английский', flag: '🇬🇧' },
  ]

  const toggles = [
    {
      id: 'age',
      label: 'Только 18+',
      description: 'Только совершеннолетние пользователи',
      value: ageFilter,
      onChange: setAgeFilter,
    },
    {
      id: 'gifts',
      label: 'Подарки обязательны',
      description: 'Пользователи должны иметь подарки в профиле',
      value: giftsRequired,
      onChange: setGiftsRequired,
    },
    {
      id: 'premium',
      label: 'Только Premium',
      description: 'Только пользователи с Telegram Premium',
      value: premiumOnly,
      onChange: (value: boolean) => {
        if (value && noPremiumOnly) {
          setNoPremiumOnly(false) // Auto-disable no_premium_only
        }
        setPremiumOnly(value)
      },
    },
    {
      id: 'no_premium',
      label: 'Только Non-Premium',
      description: 'Только пользователи БЕЗ Telegram Premium',
      value: noPremiumOnly,
      onChange: (value: boolean) => {
        if (value && premiumOnly) {
          setPremiumOnly(false) // Auto-disable premium_only
        }
        setNoPremiumOnly(value)
      },
    },
  ]

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#5288c1]/20 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-[#5288c1]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Фильтры аудитории</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Combined Filters Card */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Фильтры аудитории</h2>
            <p className="text-xs text-tg-hint mt-1">Настройка целевой аудитории</p>
          </div>

          <div className="p-4 space-y-4">
            {/* Audience Type & Language in one row */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {audienceOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      hapticFeedback('light')
                      setAudienceType(option.value)
                    }}
                    className={`py-2.5 px-3 text-sm font-medium rounded-xl border-2 transition-all ${
                      audienceType === option.value
                        ? 'bg-[#5288c1]/10 text-[#5288c1] border-[#5288c1]'
                        : 'border-[#2a3441] text-tg-hint'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {languageOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      hapticFeedback('light')
                      setLanguage(option.value)
                    }}
                    className={`py-2.5 px-2 text-xs font-medium rounded-xl border-2 transition-all ${
                      language === option.value
                        ? 'bg-[#5288c1]/10 text-[#5288c1] border-[#5288c1]'
                        : 'border-[#2a3441] text-tg-hint'
                    }`}
                  >
                    <div className="text-lg">{option.flag}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Toggles - compact */}
            <div className="divide-y divide-tg-secondary-bg">
              {toggles.map((toggle) => (
                <div
                  key={toggle.id}
                  className="flex items-center justify-between py-3 first:pt-0"
                >
                  <div className="font-medium text-tg-text">{toggle.label}</div>
                  <button
                    onClick={() => {
                      hapticFeedback('light')
                      toggle.onChange(!toggle.value)
                    }}
                    className={`relative w-12 h-7 rounded-full transition-colors ${
                      toggle.value ? 'bg-[#059669]' : 'bg-tg-bg'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        toggle.value ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">О фильтрах</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
